import { useMemo, useState } from "react";
import { Calendar as CalendarIcon, FileText, Users, ClipboardList } from "lucide-react";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Calendar } from "../ui/calendar";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "all", label: "All Time" },
  { key: "custom", label: "Custom" },
];

function startOf(period) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (period === "today") return d;
  if (period === "week") {
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1));
    return d;
  }
  if (period === "month") {
    d.setDate(1);
    return d;
  }
  return null;
}

function isPlanInRange(plan, range, customFrom, customTo) {
  if (range === "all") return true;
  const planDate = new Date(plan.date);
  planDate.setHours(0, 0, 0, 0);
  if (range === "custom") {
    if (customFrom && planDate < customFrom) return false;
    if (customTo) {
      const end = new Date(customTo);
      end.setHours(23, 59, 59, 999);
      if (planDate > end) return false;
    }
    return true;
  }
  const start = startOf(range);
  if (!start) return true;
  return planDate >= start;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function planPreview(plan) {
  const data = plan.type === "evaluation" ? plan.evaluation : plan.daily;
  return (
    data?.assessment ||
    data?.objective ||
    data?.history ||
    plan?.patient?.summary ||
    plan?.patient?.transcript ||
    "(empty)"
  );
}

export default function AllReportsView({ patients, onOpenPlan }) {
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  // Flatten all plans across patients, attach patient ref
  const allEntries = useMemo(() => {
    const entries = [];
    patients.forEach((p) => {
      p.plans.forEach((plan) => {
        entries.push({ plan, patient: p });
      });
    });
    return entries.sort((a, b) => (a.plan.date < b.plan.date ? 1 : -1));
  }, [patients]);

  const filtered = useMemo(() => {
    return allEntries.filter((e) =>
      isPlanInRange(e.plan, range, customFrom, customTo)
    );
  }, [allEntries, range, customFrom, customTo]);

  const summary = useMemo(() => {
    const uniquePatients = new Set(filtered.map((e) => e.patient.id)).size;
    const evals = filtered.filter((e) => e.plan.type === "evaluation").length;
    const dailies = filtered.filter((e) => e.plan.type === "daily").length;
    return {
      total: filtered.length,
      uniquePatients,
      evals,
      dailies,
    };
  }, [filtered]);

  // Group by date
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((entry) => {
      const key = entry.plan.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  return (
    <div data-testid="all-reports-view" className="space-y-5">
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6 md:p-8">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="font-figtree text-xs uppercase tracking-[0.18em] font-semibold text-[#059669]">
              All Reports
            </div>
            <h1 className="mt-1 font-outfit text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A]">
              Every visit, every patient.
            </h1>
            <p className="mt-1 font-figtree text-sm text-[#64748B]">
              Initials and IDs only — full names stay hidden in list views.
            </p>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`reports-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`rounded-full px-4 py-1.5 font-figtree text-xs font-medium transition-all ${
                range === r.key
                  ? "bg-[#10B981] text-white shadow-[0_4px_12px_rgba(16,185,129,0.3)]"
                  : "bg-slate-100 text-[#475569] hover:bg-slate-200"
              }`}
            >
              {r.label}
            </button>
          ))}

          {range === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="reports-custom-from"
                    className="rounded-full font-figtree text-xs border-slate-200"
                  >
                    <CalendarIcon className="w-3 h-3 mr-1.5" />
                    {customFrom ? customFrom.toLocaleDateString() : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customFrom}
                    onSelect={setCustomFrom}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="reports-custom-to"
                    className="rounded-full font-figtree text-xs border-slate-200"
                  >
                    <CalendarIcon className="w-3 h-3 mr-1.5" />
                    {customTo ? customTo.toLocaleDateString() : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customTo}
                    onSelect={setCustomTo}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total plans" value={summary.total} Icon={FileText} />
          <StatCard
            label="Unique patients"
            value={summary.uniquePatients}
            Icon={Users}
          />
          <StatCard
            label="Evaluations"
            value={summary.evals}
            Icon={ClipboardList}
          />
          <StatCard label="Daily notes" value={summary.dailies} Icon={FileText} />
        </div>
      </div>

      {/* Grouped list */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-12 text-center font-figtree text-sm text-[#94A3B8]">
          No plans in this range.
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([date, entries]) => (
            <div
              key={date}
              data-testid={`reports-day-${date}`}
              className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-5 md:p-6"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-outfit text-base font-semibold text-[#0F172A]">
                  {formatDate(date)}
                </div>
                <span className="font-figtree text-xs text-[#94A3B8]">
                  {entries.length} {entries.length === 1 ? "plan" : "plans"}
                </span>
              </div>
              <div className="space-y-2">
                {entries.map(({ plan, patient }) => (
                  <button
                    key={plan.id}
                    data-testid={`reports-entry-${plan.id}`}
                    onClick={() => onOpenPlan(plan, patient)}
                    className="w-full text-left rounded-2xl border border-slate-200 hover:border-[#10B981]/60 hover:bg-[#F0FDF4] transition-colors p-4 flex items-start gap-3"
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 text-[#475569] font-outfit text-xs font-bold flex-shrink-0">
                      {patient.initials}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-outfit text-sm font-semibold text-[#0F172A]">
                          {patient.id}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                            plan.type === "evaluation"
                              ? "bg-[#0F172A] text-white"
                              : "bg-[#D1FAE5] text-[#065F46]"
                          }`}
                        >
                          {plan.type === "evaluation" ? "Evaluation" : "Daily"}
                        </span>
                      </div>
                      <p className="font-figtree text-sm text-[#475569] line-clamp-2">
                        {planPreview(plan)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, Icon }) {
  return (
    <div className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-4">
      <Icon className="w-4 h-4 text-[#059669] mb-2" />
      <div className="font-outfit text-2xl font-bold text-[#0F172A]">{value}</div>
      <div className="font-figtree text-xs text-[#64748B] mt-0.5">{label}</div>
    </div>
  );
}
